import React from 'react';
import Typography from '@material-ui/core/Typography';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import { makeStyles } from '@material-ui/core/styles';
import { Section, Panel, DataTable, Badge, RelativeTime, StatRow, EmptyState } from './shared';
import { formatDateTime, formatDuration } from '../utils';
import { useInsightsStyles } from '../styles';

/** `state` only picks the colour; the text is always the API's `state_label`. */
export const SYNC_STATE_TONES = {
  healthy: 'green',
  running: 'blue',
  partial: 'amber',
  late: 'amber',
  failing: 'red',
  stopped: 'red',
  off: 'grey',
  no_record: 'grey',
};

const RUN_STATE_TONES = { success: 'green', running: 'blue', partial: 'amber', failure: 'red' };

const issueStyle = { color: '#b91c1c', fontSize: '.75rem', overflowWrap: 'anywhere' };

const useStyles = makeStyles(theme => ({
  block: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: 12,
    border: '1px solid var(--dm-border, #e7ebf0)',
    [theme.breakpoints.down('xs')]: { marginTop: theme.spacing(1.5), padding: theme.spacing(1.25), borderRadius: 10 },
  },
  blockHeader: { display: 'flex', alignItems: 'center', gap: theme.spacing(1), flexWrap: 'wrap', marginBottom: theme.spacing(1.25) },
  providerName: { fontSize: '.95rem', fontWeight: 700 },
  details: { background: 'var(--dm-row-alt, #f8fafc)' },
  task: { fontFamily: 'monospace' },
}));

const runColumns = [
  { key: 'started_at', label: 'Started', render: run => formatDateTime(run.started_at) },
  { key: 'duration_seconds', label: 'Duration', render: run => formatDuration(run.duration_seconds) },
  {
    key: 'state',
    label: 'Status',
    render: run => (run.state
      ? <Badge tone={RUN_STATE_TONES[run.state] || 'grey'}>{run.state.charAt(0).toUpperCase() + run.state.slice(1)}</Badge>
      : '-'),
  },
  { key: 'summary', label: 'Result', render: run => run.summary || '-' },
  { key: 'error', label: 'Error', render: run => (run.error ? <span style={issueStyle}>{run.error}</span> : '-') },
];

const jobCells = job => [
  { key: 'kind', content: job.kind_label || '-' },
  { key: 'job', content: <span style={{ fontWeight: 600 }}>{job.label}</span> },
  { key: 'state', content: <Badge tone={SYNC_STATE_TONES[job.state] || 'grey'}>{job.state_label}</Badge> },
  { key: 'last_success', content: <RelativeTime value={job.last_success_at} /> },
  { key: 'last_result', content: job.last_run?.summary || '-' },
  { key: 'next_run', content: job.schedule_enabled === false ? 'Paused' : <RelativeTime value={job.next_run_at} /> },
  { key: 'schedule', content: job.schedule_text || '-' },
  { key: 'issue', content: job.problem ? <span style={issueStyle}>{job.problem}</span> : '' },
];

const JOB_HEADERS = ['Type', 'Job', 'Status', 'Last success', 'Last result', 'Next run', 'Schedule', 'Issue'];

const JobRow = ({ job, expanded, onToggle }) => {
  const classes = useInsightsStyles();
  const ownClasses = useStyles();
  const toggle = () => onToggle(job.key);
  const Arrow = expanded ? KeyboardArrowDownIcon : KeyboardArrowRightIcon;
  return (
    <>
      <TableRow
        hover
        className={classes.clickableRow}
        tabIndex={0}
        aria-expanded={expanded}
        data-testid={`sync-job-${job.key}`}
        onClick={toggle}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); toggle(); }
        }}
      >
        <TableCell padding="checkbox"><Arrow fontSize="small" aria-hidden="true" /></TableCell>
        {jobCells(job).map(cell => <TableCell key={cell.key}>{cell.content}</TableCell>)}
      </TableRow>
      {expanded ? (
        <TableRow className={ownClasses.details}>
          <TableCell colSpan={JOB_HEADERS.length + 1}>
            <DataTable
              compact
              ariaLabel={`Recent runs: ${job.label}`}
              columns={runColumns}
              rows={job.recent_runs || []}
              getRowKey={(run, index) => run.started_at || index}
              emptyText={job.state_label}
            />
            {job.task ? <Typography className={classes.footnote}>Task: <span className={ownClasses.task}>{job.task}</span></Typography> : null}
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
};

const ProviderBlock = ({ provider }) => {
  const classes = useInsightsStyles();
  const ownClasses = useStyles();
  const [expanded, setExpanded] = React.useState(() => new Set());
  const jobs = [...(provider.course_jobs || []), ...(provider.report_jobs || [])];
  const toggle = key => setExpanded(previous => {
    const next = new Set(previous);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  return (
    <div className={ownClasses.block} data-testid={`sync-provider-${provider.key}`}>
      <div className={ownClasses.blockHeader}>
        <Typography component="h3" className={ownClasses.providerName}>{provider.provider}</Typography>
        <Badge tone={SYNC_STATE_TONES[provider.state] || 'grey'}>{provider.state_label}</Badge>
      </div>
      {provider.note ? <Typography className={classes.description} style={{ marginTop: -4, marginBottom: 10 }}>{provider.note}</Typography> : null}
      <div className={classes.tableWrap}>
        <Table size="small" className={classes.table} aria-label={`${provider.provider} sync jobs`} style={{ minWidth: 960 }}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              {JOB_HEADERS.map(header => <TableCell key={header}>{header}</TableCell>)}
            </TableRow>
          </TableHead>
          <TableBody>
            {jobs.map(job => (
              <JobRow key={`${job.kind}-${job.key}`} job={job} expanded={expanded.has(job.key)} onToggle={toggle} />
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

/** `sync_health` section: course catalogue and learner progress jobs, grouped by the six course providers. */
const SyncHealth = ({ data = {} }) => {
  const classes = useInsightsStyles();
  const providers = data.providers || [];
  const totals = (data.totals || [])
    .filter(item => Number(item.value) > 0)
    .map(item => ({ ...item, tone: SYNC_STATE_TONES[item.key] }));
  return (
    <Section id="sync_health" title="Provider sync health" exportKey="sync_health">
      <Panel>
        {totals.length ? <StatRow items={totals} /> : null}
        {data.description ? <Typography className={classes.description} style={totals.length ? undefined : { marginTop: 0 }}>{data.description}</Typography> : null}
        {providers.length
          ? providers.map(provider => <ProviderBlock key={provider.key} provider={provider} />)
          : <div style={{ marginTop: 16 }}><EmptyState /></div>}
        {data.note ? <Typography className={classes.footnote}>{data.note}</Typography> : null}
      </Panel>
    </Section>
  );
};

export default SyncHealth;
